// Import changes to review (D-032, B-026): a catalogue import wanted to change a field a
// person had changed here, so it left the person's value alone and asks. Keep yours, or take
// the import's (taking an import's recipe makes a new draft for the baker to sign off).

import type { CatalogueReviewItem } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import { type AdminArea, canOpenArea } from '@enp/domain/permissions';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  cn,
  EmptyState,
  focusRing,
  PageHeader,
  Skeleton,
  StatusBadge,
  toast,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, FileInput, GitCompareArrows, Keyboard, UserPen } from 'lucide-react';
import { useMemo, useRef, type KeyboardEvent } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { ProblemNote, useCan } from '../../../production/parts.js';
import {
  invalidateProduction,
  resolveReviewItem,
  reviewItemsQuery,
  reviewSearchSchema,
} from '../../../production/queries.js';
import { ChangeList } from '../../../production/recipe-dialogs.js';
import { fieldLabel, reviewRecipeChanges, reviewValueText } from '../../../production/words.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { isTypingTarget } from '../../../shell/shortcuts.js';
import { LoadMoreFooter } from '../../../stock/components.js';
import { actionProblem } from '../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/catalogue/review')({
  validateSearch: reviewSearchSchema,
  component: ReviewPage,
});

/** The area each reviewed thing lives in: its link is shown only to those who can open it. */
const ENTITY_AREA: Record<CatalogueReviewItem['entity']['table'], AdminArea> = {
  product: 'catalogue',
  recipe: 'recipes',
  supplier: 'purchasing',
};

function EntityLink({ item }: { item: CatalogueReviewItem }) {
  const { data: me } = useQuery(meQueryOptions());
  const cls = 'font-medium text-primary-text underline-offset-4 hover:underline';
  // No link into a section the person can't open (D-070, CR8B-32): the name alone.
  if (!me || !canOpenArea(me.permissions, ENTITY_AREA[item.entity.table])) {
    return <span className="font-medium text-fg">{item.entity.name}</span>;
  }
  switch (item.entity.table) {
    case 'product':
      return (
        <Link to="/catalogue/$productId" params={{ productId: item.entity.id }} className={cls}>
          {item.entity.name}
        </Link>
      );
    case 'recipe':
      return (
        <Link to="/recipes/$recipeId" params={{ recipeId: item.entity.id }} className={cls}>
          {item.entity.name}
        </Link>
      );
    case 'supplier':
      return (
        <Link
          to="/purchasing/suppliers/$supplierId"
          params={{ supplierId: item.entity.id }}
          className={cls}
        >
          {item.entity.name}
        </Link>
      );
  }
}

/** The two choices, in key order: nothing is chosen until someone chooses (UI8A-3, UI1A-3). */
const CHOICES = [
  { resolution: 'keep', label: 'Keep mine' },
  { resolution: 'take', label: 'Take the import’s' },
] as const;

function ReviewCard({ item, canDecide }: { item: CatalogueReviewItem; canDecide: boolean }) {
  const queryClient = useQueryClient();
  const card = useRef<HTMLElement>(null);
  // Where the focus goes once this card has left the waiting list: the next card, else the one
  // before. Only when the decision was made from inside the card (the keyboard flow).
  const next = useRef<HTMLElement | null>(null);
  const resolve = useMutation({
    mutationFn: (resolution: 'keep' | 'take') => resolveReviewItem(item.id, resolution),
    onSuccess: async (done) => {
      await invalidateProduction(queryClient);
      next.current?.focus();
      toast.success(
        done.resolution === 'kept'
          ? `Kept yours: ${fieldLabel(item.field).toLowerCase()} of ${item.entity.name}`
          : `Took the import’s ${fieldLabel(item.field).toLowerCase()} for ${item.entity.name}`,
        {
          description:
            item.entity.table === 'recipe' && done.resolution === 'taken'
              ? 'It’s a new draft now: the baker signs it off before it’s used.'
              : done.resolution === 'kept'
                ? 'Later imports keep asking before they change it.'
                : 'Imports update it again from now on.',
        },
      );
    },
  });
  const failed = resolve.isError ? actionProblem(resolve.error, 'It wasn’t decided') : null;
  const open = item.status === 'open';
  const headingId = `review-${item.id}`;
  // A recipe is judged by what it changes, not by its line count (UI5A-105).
  const recipeChanges = reviewRecipeChanges(item);
  const decidable = open && canDecide;
  const decide = (resolution: 'keep' | 'take') => {
    const li = card.current?.closest('li');
    const sibling = li?.nextElementSibling ?? li?.previousElementSibling;
    next.current = card.current?.contains(document.activeElement)
      ? (sibling?.querySelector<HTMLElement>('[data-review-card]') ?? null)
      : null;
    resolve.mutate(resolution);
  };
  // 1 keeps yours, 2 takes the import's, as the import review's number keys (UI8A-3).
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!decidable || resolve.isPending || e.ctrlKey || e.metaKey || e.altKey) return;
    if (isTypingTarget(e.target)) return;
    const choice = /^[12]$/.test(e.key) ? CHOICES[Number(e.key) - 1] : undefined;
    if (choice) {
      e.preventDefault();
      decide(choice.resolution);
    }
  };
  return (
    <li>
      <article
        ref={card}
        tabIndex={0}
        data-review-card
        aria-labelledby={headingId}
        onKeyDown={onKeyDown}
        className={cn(
          'flex flex-col gap-4 rounded-lg border border-border bg-surface-raised p-5',
          focusRing,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 id={headingId} className="font-display text-base font-semibold text-fg">
              {fieldLabel(item.field)} of <EntityLink item={item} />
            </h2>
            <p className="text-sm text-muted">
              The import of <AccountDate value={item.created_at} withTime /> wanted to change it.
            </p>
          </div>
          {!open && (
            <StatusBadge status="success">
              {item.resolution === 'kept' ? 'Kept yours' : 'Took the import’s'}
            </StatusBadge>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
          <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-sunken p-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
              <UserPen aria-hidden className="size-3.5" />
              {open ? 'Yours, in use now' : 'Yours'}
            </span>
            <span className="text-body text-fg" data-testid="current-value">
              {reviewValueText(item, item.current_value)}
            </span>
            {item.owner && (
              <span className="text-xs text-muted">
                Changed by {item.owner.name ?? 'a person'}
                {item.owned_at && (
                  <>
                    {' '}
                    <AccountDate value={item.owned_at} />
                  </>
                )}
              </span>
            )}
          </div>
          <ArrowRight aria-hidden className="hidden size-5 self-center text-muted md:block" />
          <div className="flex flex-col gap-1 rounded-md border border-border p-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
              <FileInput aria-hidden className="size-3.5" />
              From the import
            </span>
            <span className="text-body text-fg" data-testid="import-value">
              {reviewValueText(item, item.import_value)}
            </span>
            {item.entity.table === 'recipe' && (
              <span className="text-xs text-muted">
                Taking it makes a new draft; the version in use stays until the baker signs the
                draft off.
              </span>
            )}
          </div>
        </div>
        {recipeChanges && (
          <section
            aria-labelledby={`${headingId}-changes`}
            className="flex flex-col gap-2 rounded-md border border-border bg-surface-sunken p-3"
          >
            <h3 id={`${headingId}-changes`} className="text-sm font-semibold text-fg">
              What the import’s recipe changes against yours
            </h3>
            <ChangeList
              changes={recipeChanges}
              label="What the import changes"
              same="The same ingredients and quantities as yours."
            />
          </section>
        )}
        {failed && <ProblemNote problem={failed} />}
        {decidable && (
          // Both look the same, so no card reads as already decided (UI8A-3).
          <div role="group" aria-label="Decisions" className="flex flex-wrap gap-2">
            {CHOICES.map((c, i) => (
              <Button
                key={c.resolution}
                variant="secondary"
                aria-keyshortcuts={String(i + 1)}
                loading={resolve.isPending && resolve.variables === c.resolution}
                disabled={resolve.isPending}
                onClick={() => decide(c.resolution)}
              >
                <kbd
                  aria-hidden
                  className="rounded border border-current/30 px-1 font-mono text-xs leading-4"
                >
                  {i + 1}
                </kbd>
                {c.label}
              </Button>
            ))}
          </div>
        )}
        {!open && item.resolved_at && (
          <p className="text-sm text-muted">
            Decided by {item.resolved_by?.name ?? 'someone'}{' '}
            <AccountDate value={item.resolved_at} withTime />.
          </p>
        )}
      </article>
    </li>
  );
}

function ReviewPage() {
  useDocumentTitle('Import changes to review');
  const search = Route.useSearch();
  const status = search.status ?? 'open';
  const canDecide = useCan('catalogue.edit', true);
  const list = useInfiniteQuery({
    ...reviewItemsQuery(status),
    placeholderData: keepPreviousData,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total;
  // Both tabs show their count (UI8A-9): the other tab's first page says how many it has.
  const other = useInfiniteQuery(reviewItemsQuery(status === 'open' ? 'resolved' : 'open'));
  const otherTotal = other.data?.pages[0]?.total;
  const totals: Record<'open' | 'resolved', number | undefined> =
    status === 'open'
      ? { open: total, resolved: otherTotal }
      : { open: otherTotal, resolved: total };
  // j/k or the arrows move between cards, as on the import review (UI8A-3).
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
    const all = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-review-card]')];
    const at = all.findIndex((c) => c.contains(document.activeElement ?? null));
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      all[Math.min(all.length - 1, at + 1)]?.focus();
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      all[Math.max(0, at - 1)]?.focus();
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/catalogue">Catalogue</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Import changes to review</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
        title="Import changes to review"
        description="An import never overwrites what a person changed here. When it brings a different value, it asks: keep yours, or take the import’s."
      />
      <nav aria-label="Review views" className="mb-4 flex flex-wrap gap-1 border-b border-border">
        {(
          [
            ['open', 'Waiting'],
            ['resolved', 'Decided'],
          ] as const
        ).map(([id, label]) => (
          <Link
            key={id}
            to="/catalogue/review"
            search={id === 'open' ? {} : { status: 'resolved' }}
            aria-current={status === id ? 'page' : undefined}
            className={cn(
              '-mb-px inline-flex h-control shrink-0 items-center gap-2 border-b-2 border-transparent px-3 text-body font-medium text-muted hover:text-fg',
              status === id && 'border-primary text-fg',
              focusRing,
            )}
          >
            {label}
            {totals[id] !== undefined && ` (${formatCount(totals[id])})`}
          </Link>
        ))}
      </nav>
      {!canDecide && status === 'open' && items.length > 0 && (
        <p className="mb-4 text-sm text-muted">
          Deciding needs the “edit the catalogue” permission and access to every branch.
        </p>
      )}

      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The changes to review"
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <span role="status" className="sr-only">
            Loading the changes to review…
          </span>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-lg" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon={GitCompareArrows}
          title={status === 'open' ? 'Nothing to review' : 'Nothing decided yet'}
          description={
            status === 'open'
              ? 'No import has tried to change something a person changed here. When one does, it shows here instead of overwriting it.'
              : 'Decisions you make on waiting changes are listed here.'
          }
          action={
            <Button variant="secondary" asChild>
              <Link to="/catalogue">Back to the catalogue</Link>
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <p className="flex items-center gap-2 text-sm text-muted">
            <Keyboard aria-hidden className="size-4" />
            <span>
              <kbd className="font-mono">j</kbd>/<kbd className="font-mono">k</kbd> or arrows to
              move
              {status === 'open' && canDecide && (
                <>
                  {' '}
                  · <kbd className="font-mono">1</kbd> to keep yours ·{' '}
                  <kbd className="font-mono">2</kbd> to take the import’s
                </>
              )}
            </span>
          </p>
          <ul aria-label="Changes to review" className="flex flex-col gap-4" onKeyDown={onKeyDown}>
            {items.map((i) => (
              <ReviewCard key={i.id} item={i} canDecide={canDecide} />
            ))}
          </ul>
          <LoadMoreFooter
            loaded={items.length}
            total={total}
            noun={['change', 'changes']}
            hasMore={list.hasNextPage}
            loading={list.isFetchingNextPage}
            onMore={() => void list.fetchNextPage()}
          />
        </div>
      )}
    </>
  );
}
